import React, { useMemo } from 'react';

/**
 * DynamicProblemTree - Automatic layout calculation for problem tree nodes
 *
 * This component takes a graph structure of nodes with parent-child relationships
 * and automatically calculates optimal positions using a hierarchical layout algorithm.
 */

export interface ProblemNode {
  id: string;
  children?: string[];
}

export interface ProblemTreeData {
  nodes: ProblemNode[];
}

export interface NodePosition {
  x: number;
  y: number;
  level: number;
  order: number;
}

export interface Connection {
  from: string;
  to: string;
  path: string;
}

export interface ProblemTreeLayout {
  nodePositions: Map<string, NodePosition>;
  levels: string[][];
  connections: Connection[];
  nodeSize: number;
  viewBoxDimensions: {
    minX: number;
    minY: number;
    width: number;
    height: number;
  };
}

/**
 * Calculate the hierarchical layout for a problem tree
 */
export function calculateProblemTreeLayout(
  problemData: ProblemTreeData,
  nodeSize: number = 48,
  horizontalGap: number = 30,
  verticalGap: number = 60,
): ProblemTreeLayout {
  // Build adjacency map
  const nodeMap = new Map<string, ProblemNode>();
  const childToParents = new Map<string, string[]>();

  problemData.nodes.forEach(node => {
    nodeMap.set(node.id, node);
    node.children?.forEach(childId => {
      if (!childToParents.has(childId)) {
        childToParents.set(childId, []);
      }
      childToParents.get(childId)!.push(node.id);
    });
  });

  // Calculate levels for DAG (Directed Acyclic Graph)
  // For nodes with multiple parents, place them at max(parent levels) + 1
  const nodeLevel = new Map<string, number>();
  const rootNode = problemData.nodes[0];

  // Track the maximum level proposed for each node
  const maxProposedLevel = new Map<string, number>();
  const queue: Array<{ id: string; level: number }> = [{ id: rootNode.id, level: 0 }];
  const addedToQueue = new Set<string>();
  addedToQueue.add(rootNode.id);

  // First pass: calculate maximum level for each node
  while (queue.length > 0) {
    const { id, level } = queue.shift()!;

    // Update the maximum proposed level for this node
    const currentMax = maxProposedLevel.get(id) || 0;
    maxProposedLevel.set(id, Math.max(currentMax, level));

    const node = nodeMap.get(id);
    node?.children?.forEach(childId => {
      // Always add children to queue, even if already seen
      // This ensures we consider all parent-child paths
      queue.push({ id: childId, level: level + 1 });
      addedToQueue.add(childId);
    });
  }

  // Second pass: assign nodes to levels based on maximum proposed level
  const levels: string[][] = [];
  for (const [nodeId, level] of maxProposedLevel.entries()) {
    if (!levels[level]) levels[level] = [];
    levels[level].push(nodeId);
    nodeLevel.set(nodeId, level);
  }

  // Check for any nodes that weren't reached (disconnected from root)
  problemData.nodes.forEach(node => {
    if (!nodeLevel.has(node.id)) {
      console.warn(`[DynamicProblemTree] Node '${node.id}' is not reachable from root`);
      // Add to a new level at the end
      const maxLevel = levels.length;
      if (!levels[maxLevel]) levels[maxLevel] = [];
      levels[maxLevel].push(node.id);
      nodeLevel.set(node.id, maxLevel);
    }
  });

  // Calculate positions
  const positions = new Map<string, NodePosition>();

  // First pass: assign initial positions to determine order
  levels.forEach((levelNodes, levelIndex) => {
    levelNodes.forEach((nodeId, indexInLevel) => {
      const x = indexInLevel * (nodeSize + horizontalGap);
      const y = levelIndex * (nodeSize + verticalGap);
      positions.set(nodeId, { x, y, level: levelIndex, order: indexInLevel });
    });
  });

  // Calculate desired positions based on parent/child relationships
  // More iterations = better centering of nodes over their children/parents
  for (let iteration = 0; iteration < 4; iteration++) {
    // Bottom-up: adjust order based on parents
    for (let levelIndex = levels.length - 1; levelIndex > 0; levelIndex--) {
      const levelNodes = levels[levelIndex];

      levelNodes.forEach(nodeId => {
        const parents = childToParents.get(nodeId) || [];
        if (parents.length > 0) {
          const parentXSum = parents.reduce((sum, parentId) => {
            return sum + (positions.get(parentId)?.x || 0);
          }, 0);
          const avgParentX = parentXSum / parents.length;

          const currentPos = positions.get(nodeId)!;
          const newX = currentPos.x * 0.5 + avgParentX * 0.5;
          positions.set(nodeId, { ...currentPos, x: newX });
        }
      });
    }

    // Top-down: adjust order based on children
    for (let levelIndex = 0; levelIndex < levels.length - 1; levelIndex++) {
      const levelNodes = levels[levelIndex];

      levelNodes.forEach(nodeId => {
        const node = nodeMap.get(nodeId);
        if (node?.children && node.children.length > 0) {
          const childXSum = node.children.reduce((sum, childId) => {
            return sum + (positions.get(childId)?.x || 0);
          }, 0);
          const avgChildX = childXSum / node.children.length;

          const currentPos = positions.get(nodeId)!;
          const newX = currentPos.x * 0.5 + avgChildX * 0.5;
          positions.set(nodeId, { ...currentPos, x: newX });
        }
      });
    }
  }

  // Snap nodes to fractional grid positions within each level
  levels.forEach((levelNodes, levelIndex) => {
    // Sort nodes by their calculated x position
    const sortedNodes = [...levelNodes].sort((a, b) => {
      return positions.get(a)!.x - positions.get(b)!.x;
    });

    const numNodes = sortedNodes.length;
    const levelWidth = (numNodes - 1) * (nodeSize + horizontalGap);

    // Snap to evenly distributed positions
    sortedNodes.forEach((nodeId, index) => {
      const currentPos = positions.get(nodeId)!;
      let snappedX: number;

      if (numNodes === 1) {
        snappedX = 0;
      } else {
        // Evenly distribute across the level width
        snappedX = -levelWidth / 2 + index * (nodeSize + horizontalGap);
      }

      positions.set(nodeId, {
        ...currentPos,
        x: snappedX,
        y: levelIndex * (nodeSize + verticalGap),
      });
    });
  });

  // Generate connections with curved paths
  const connections: Connection[] = [];
  problemData.nodes.forEach(node => {
    node.children?.forEach(childId => {
      const fromPos = positions.get(node.id);
      const toPos = positions.get(childId);
      if (fromPos && toPos) {
        const fromX = fromPos.x + nodeSize / 2;
        const fromY = fromPos.y + nodeSize;
        const toX = toPos.x + nodeSize / 2;
        const toY = toPos.y;

        // Calculate control points for smooth curves
        const midY = (fromY + toY) / 2;

        connections.push({
          from: node.id,
          to: childId,
          path: `M ${fromX} ${fromY} C ${fromX} ${midY}, ${toX} ${midY}, ${toX} ${toY}`,
        });
      } else {
        console.warn(`[DynamicProblemTree] Missing position for connection ${node.id} → ${childId}`, {
          fromPos,
          toPos,
        });
      }
    });
  });

  // Calculate viewBox dimensions with extra padding for overflow content
  const viewBoxPadding = 60;
  const allPositions = Array.from(positions.values());
  const minX = Math.min(...allPositions.map(p => p.x)) - viewBoxPadding;
  const maxX = Math.max(...allPositions.map(p => p.x)) + nodeSize + viewBoxPadding;
  const minY = Math.min(...allPositions.map(p => p.y)) - viewBoxPadding;
  const maxY = Math.max(...allPositions.map(p => p.y)) + nodeSize + viewBoxPadding;

  const viewBoxWidth = maxX - minX;
  const viewBoxHeight = maxY - minY;

  return {
    nodePositions: positions,
    levels,
    connections,
    nodeSize,
    viewBoxDimensions: {
      minX,
      minY,
      width: viewBoxWidth,
      height: viewBoxHeight,
    },
  };
}

export interface DynamicProblemTreeProps {
  problemData: ProblemTreeData;
  nodeSize?: number;
  horizontalGap?: number;
  verticalGap?: number;
  renderNode: (nodeId: string, position: NodePosition) => React.ReactNode;
  /** Optional function to check if a node is unlocked (for connection styling) */
  isNodeUnlocked?: (nodeId: string) => boolean;
  className?: string;
}

/**
 * DynamicProblemTree component - renders a problem tree with automatic layout
 */
export const DynamicProblemTree: React.FC<DynamicProblemTreeProps> = ({
  problemData,
  nodeSize = 48,
  horizontalGap = 30,
  verticalGap = 60,
  renderNode,
  isNodeUnlocked,
  className = '',
}) => {
  const layout = useMemo(
    () => calculateProblemTreeLayout(problemData, nodeSize, horizontalGap, verticalGap),
    [problemData, nodeSize, horizontalGap, verticalGap],
  );

  const { nodePositions, connections, viewBoxDimensions } = layout;
  const { minX, minY, width, height } = viewBoxDimensions;

  return (
    <div className={`tw-relative tw-flex-1 tw-w-full ${className}`} style={{ overflow: 'visible' }}>
      <svg
        className="tw-w-full tw-h-full"
        viewBox={`${minX} ${minY} ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: 'visible' }}
      >
        <defs>
          {/* Shadow filter definition */}
          <filter id="dynamicShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" />
            <feOffset dx="0" dy="1" result="offsetblur" />
            <feComponentTransfer>
              <feFuncA type="linear" slope="0.15" />
            </feComponentTransfer>
            <feMerge>
              <feMergeNode />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Draw connections */}
        <g className="connections">
          {connections.map((conn, idx) => {
            // Check if the source node is unlocked
            const isSourceUnlocked = isNodeUnlocked?.(conn.from) ?? false;
            const strokeColor = isSourceUnlocked ? '#22c55e' : 'rgba(156, 155, 155, 1.00)';
            const strokeDasharray = isSourceUnlocked ? 'none' : '5,5';

            return (
              <path
                key={`conn-${conn.from}-${conn.to}-${idx}`}
                d={conn.path}
                stroke={strokeColor}
                strokeWidth="2.5"
                fill="none"
                strokeLinecap="round"
                strokeDasharray={strokeDasharray}
              />
            );
          })}
        </g>

        {/* Draw nodes */}
        <g className="nodes">
          {Array.from(nodePositions.entries()).map(([nodeId, pos]) => (
            <g key={nodeId} transform={`translate(${pos.x}, ${pos.y})`}>
              <foreignObject x="0" y="0" width={nodeSize} height={nodeSize} style={{ overflow: 'visible' }}>
                {renderNode(nodeId, pos)}
              </foreignObject>
            </g>
          ))}
        </g>
      </svg>
    </div>
  );
};
